import classnames from 'classnames';
import React, { memo, useCallback, useMemo, useRef, useState, } from 'react';
import { useDebounce } from 'react-use';
import FormControlErrorMessage from '../../utils/FormControlErrorMessage';
import FormControlInfoMessage from '../../utils/FormControlInfoMessage';
import Icon from '../../utils/Icon';
import useT from '../../utils/Translations/useT';
import useHelpNoteTitle from '../../utils/Translations/useHelpNoteTitle';
import AnimatedTitle from './AnimatedTitle';
import styles from './TextArea.scss';
import { isRTL } from '../../../utils/commonHelper';
const DEFAULT_ROWS = 2;
const TIMEOUT = 200;
const TextArea = ({ onChange, onPaste, onBlur, rows = DEFAULT_ROWS, name, value = '', leftIcon = null, errorMessage = null, label, placeholder, inputProps = {}, disabled = false, animateTitle = false, formElement = true, required = false, className = '', wrapperClassName = '', infoMessage = null, infoMessageAlign = 'left', onKeyDown, handleSelect, onMouseUp, autoHeight = true, maxLength, minLength = 0, isControl = true, hasCharactersCounter = true, infoMessageClassName = '', searchQuery, dataCy, tooltipTitle, }) => {
    const t = useT();
    const resolvedTooltipTitle = useHelpNoteTitle(label || placeholder, tooltipTitle);
    const textareaRef = useRef(null);
    const [isFocus, setIsFocus] = useState(false);
    const wrapperClasses = useMemo(() => classnames({
        'form-group pb-10': formElement,
        'has-feedback': !!leftIcon,
        'has-feedback-left': !!leftIcon,
        'form-group-material': animateTitle,
    }, wrapperClassName), [animateTitle, formElement, leftIcon, wrapperClassName]);
    const _animateTitle = useMemo(() => animateTitle ? (<AnimatedTitle isRequired={required} placeholder={label || placeholder} tooltipTitle={tooltipTitle} value={value}/>) : null, [animateTitle, placeholder, label, required, tooltipTitle, value]);
    const _errorMessage = useMemo(() => errorMessage ? (<FormControlErrorMessage errorMessage={errorMessage}/>) : null, [errorMessage]);
    const _infoMessage = useMemo(() => {
        if (errorMessage) {
            return null;
        }
        if (infoMessage) {
            return (<FormControlInfoMessage additionalClassNames={infoMessageClassName} message={infoMessage} messageAlign={infoMessageAlign}/>);
        }
        if (maxLength && hasCharactersCounter && isFocus) {
            return (<FormControlInfoMessage message={t('#{length}/#{maxLength}', {
                length: value ? `${value}`.length : 0,
                maxLength,
            })} messageAlign="right_end"/>);
        }
        return null;
    }, [
        value,
        t,
        maxLength,
        errorMessage,
        infoMessage,
        infoMessageAlign,
        hasCharactersCounter,
        infoMessageClassName,
        isFocus,
    ]);
    const _leftIcon = useMemo(() => leftIcon ? (<div className="form-control-feedback">
          <Icon className="text-muted" name={leftIcon}/>
        </div>) : null, [leftIcon]);
    const setHeight = useCallback(() => {
        if (autoHeight && textareaRef.current) {
            const prevHeight = Number(textareaRef.current.style.height.replace('px', ''));
            if (prevHeight > textareaRef.current.scrollHeight) {
                return;
            }
            textareaRef.current.style.overflow = 'hidden';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [autoHeight, textareaRef]);
    const delayedResize = useCallback(() => {
        if (autoHeight) {
            setTimeout(setHeight, 0);
        }
    }, [autoHeight, setHeight]);
    useDebounce(() => {
        value && delayedResize();
    }, TIMEOUT, [delayedResize, value]);
    const handleChange = useCallback(e => {
        delayedResize();
        onChange && onChange(e);
    }, [onChange, delayedResize]);
    const handlePaste = event => {
        onPaste && onPaste(event, name);
    };
    const handleBlur = useCallback(e => {
        onBlur && onBlur(e);
    }, []);
    const _onMouseUp = useCallback(e => {
        onMouseUp && onMouseUp(e);
    }, []);
    const handleDivBlur = useCallback(e => {
        setIsFocus(false);
        onBlur && onBlur(e);
    }, [onBlur]);
    const handleFocus = useCallback(e => {
        setIsFocus(true);
    }, []);
    const rtlClass = useMemo(() => (isRTL(value) ? styles.rtl : ``), [value]);
    const _handleSelect = useCallback(event => {
        handleSelect &&
            handleSelect(event.target.value.substring(event.target.selectionStart, event.target.selectionEnd));
    }, [handleSelect]);
    const getHighlightedText = (text, searchQuery) => {
        if (!searchQuery)
            return text;
        const regex = new RegExp(`(${searchQuery})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) => regex.test(part) ? (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} className={styles.highLightText}>
          {part}
        </span>) : (part));
    };
    return (<div className={wrapperClasses} onBlur={handleDivBlur}>
      {_animateTitle}
      <div className={styles.textAreaWrapper}>
        <div aria-hidden="true" className={classnames('no-padding', styles.textAreaOverlay, rtlClass, className)}>
          {getHighlightedText(value, searchQuery)}
        </div>
        <textarea ref={textareaRef} className={classnames({ 'form-control': isControl }, styles.TextAreaField, rtlClass, className)} data-cy={dataCy} disabled={disabled} maxLength={maxLength} minLength={minLength} name={name} placeholder={required ? `${placeholder} *` : placeholder} rows={rows} title={resolvedTooltipTitle} value={value} wrap="hard" onBlur={handleBlur} onChange={handleChange} onFocus={handleFocus} onKeyDown={onKeyDown} onMouseUp={_onMouseUp} onPaste={handlePaste} onSelect={_handleSelect} {...inputProps}/>
      </div>
      {_errorMessage}
      {_infoMessage}
      {_leftIcon}
    </div>);
};
export default memo(TextArea);
